import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import Edit_icon from "../../Images/Edit_icon.svg";
import pdfIcon from "../../Images/pdf_icon.svg";
import AssignmentCreate from "./DemoModal";
import axios from "axios";
import { API_URL, getAuthHeaders } from "../../config/api";
import Calendar from "../../Images/calendar.svg";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ToastMessage from "../../config/ToastMessage";

export default function DemoModal({
  isOpen = true,
  onClose = () => {},
  selectedLesson,
}) {
  const lessonId = selectedLesson?.lessonDbId ?? null;

  const [activeTab, setActiveTab] = useState("publish");
  const [showEditPopup, setShowEditPopup] = useState(false);

  const [demos, setDemos] = useState([]);
  const [selectedDemo, setSelectedDemo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scheduleData, setScheduleData] = useState(null);
  const [currentDemoAssignment, setCurrentDemoAssignment] = useState(null);
  const [toast, setToast] = useState({
    show: false,
    type: "error",
    message: "",
  });

  // Publisher form data that will be passed to Assignment Modal
  const [formData, setFormData] = useState({
    gradePostingDate: "",
    startDate: "",
    endDate: "",
    allowResubmission: true,
    maxAttempts: false,
    lateSubmission: false,
    batch: selectedLesson?.schedule_id || "", // Initialize with schedule_id
  });

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };
  const handlePublish = async () => {
    if (!selectedDemo) {
      setToast({
        show: true,
        type: "error",
        message: "Please select a demo to publish.",
      });
      return;
    }

    if (!selectedLesson?.schedule_id) {
      setToast({
        show: true,
        type: "error",
        message: "Schedule ID not available.",
      });
      return;
    }

    if (!formData.startDate) {
      setToast({
        show: true,
        type: "error",
        message: "Please select a start date.",
      });
      return;
    }

    if (!formData.endDate) {
      setToast({
        show: true,
        type: "error",
        message: "Please select an end date.",
      });
      return;
    }

    try {
      const submissionControls = {
        allowResubmission: formData.allowResubmission,
        maxAttempts: formData.maxAttempts,
        lateSubmission: formData.lateSubmission,
      };

      const payload = {
        demo_id: selectedDemo.demo_id,
        schedule_id: parseInt(selectedLesson.schedule_id),
        start_date: formatDate(formData.startDate),
        end_date: formatDate(formData.endDate),
        grade_posting_date: formData.gradePostingDate
          ? formatDate(formData.gradePostingDate)
          : null,
        submission_controls: JSON.stringify(submissionControls),
        status: "ACTIVE",
      };

      const response = await axios.post(`${API_URL}/demo/assign`, payload, {
        headers: getAuthHeaders(),
      });

      if (response.data?.success) {
        setToast({
          show: true,
          type: "success",
          message: response.data.message || "Demo published successfully!",
        });

        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        setToast({
          show: true,
          type: "error",
          message: response.data?.message || "Failed to publish demo.",
        });
      }
    } catch (error) {
      console.error("Error publishing demo:", error);

      let errorMessage = "An error occurred while publishing the demo.";

      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      setToast({
        show: true,
        type: "error",
        message: errorMessage,
      });
    }
  };

  const handleCheckbox = (field) => {
    setFormData((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleEditDemo = async (demoId) => {
    if (!demoId) {
      console.warn("Invalid demo ID");
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/demo/${demoId}`);

      if (res.data?.success) {
        setSelectedDemo(res.data.data);
        setShowEditPopup(true);
      }
    } catch (err) {
      console.error(err);
    }
  };

  // Handle creating new demo - only if no demo exists
  const handleCreateNewDemo = () => {
    // Check if demo already exists
    if (demos.length > 0) {
      alert(
        "This lesson already has a demo. Only one demo is allowed per lesson.",
      );
      return;
    }
    setSelectedDemo(null); // Clear selection for new demo
    setShowEditPopup(true);
  };

  // Callback when demo is saved
  const handleDemoSaved = (savedDemo) => {
    // Refresh demos list
    fetchDemos();
    setShowEditPopup(false);
  };

  // Fetch demo assignment data for selected demo
  const fetchDemoAssignment = async (demoId) => {
    if (!selectedLesson?.schedule_id || !demoId) {
      setCurrentDemoAssignment(null);
      return;
    }

    try {
      const res = await axios.get(
        `${API_URL}/demo/schedules/${selectedLesson.schedule_id}`,
        {
          headers: getAuthHeaders(),
        },
      );

      if (res.data?.success && res.data.data?.demos) {
        const demoAssignment = res.data.data.demos.find(
          (demo) => demo.demo_id === demoId,
        );
        setCurrentDemoAssignment(demoAssignment || null);
      } else {
        setCurrentDemoAssignment(null);
      }
    } catch (err) {
      console.error("Demo assignment fetch error:", err);
      setCurrentDemoAssignment(null);
    }
  };

  //FETCH DEMOS SAFELY
  const fetchDemos = async () => {
    if (!lessonId) {
      setDemos([]);
      setSelectedDemo(null);
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/demo/lesson/${lessonId}`);

      if (res.data?.success) {
        setDemos(res.data.data || []);
        setSelectedDemo(res.data.data?.[0] || null);
      } else {
        setDemos([]);
        setSelectedDemo(null);
      }
    } catch (err) {
      console.error("Demo fetch error:", err);
      setDemos([]);
      setSelectedDemo(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDemos();
  }, [lessonId]);

  // Reset formData when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        gradePostingDate: "",
        startDate: "",
        endDate: "",
        allowResubmission: true,
        maxAttempts: false,
        lateSubmission: false,
        batch: selectedLesson?.schedule_id || "",
      });
      setCurrentDemoAssignment(null); // Reset current assignment
    }
  }, [isOpen, selectedLesson?.schedule_id]);

  // Update formData.batch when selectedLesson changes
  useEffect(() => {
    if (selectedLesson?.schedule_id) {
      console.log("Schedule ID:", selectedLesson.schedule_id);
      setFormData((prev) => ({ ...prev, batch: selectedLesson.schedule_id }));
    }
  }, [selectedLesson?.schedule_id]);

  useEffect(() => {
    const fetchScheduleData = async () => {
      if (!selectedLesson?.schedule_id) {
        console.log("No schedule_id available");
        return;
      }

      try {
        const response = await axios.get(
          `${API_URL}/course-schedules/${selectedLesson.schedule_id}`,
          { headers: getAuthHeaders() },
        );

        console.log("Schedule Data:", response.data);
        if (response.data?.success) {
          setScheduleData(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching schedule:", error);
      }
    };

    fetchScheduleData();
  }, [selectedLesson?.schedule_id]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        {/* HEADER */}
        <div className="flex items-center justify-between px-9 py-5 border-b sticky top-0 bg-white z-10">
          <h2 className="text-2xl font-bold text-gray-900">Demo Publication</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5 text-gray-700" />
          </button>
        </div>

        {/* COURSE INFO */}
        <div className="bg-gray-100 border rounded m-6 p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-gray-700 mb-1">Program</p>
              <h2 className="font-semibold">
                {selectedLesson?.courseTitle || "—"}
              </h2>

              <p className="text-sm text-gray-700 mt-3 mb-1">
                Module {selectedLesson?.moduleDisplayId ?? "-"}
              </p>
              <h3 className="font-semibold">
                {selectedLesson?.moduleTitle || selectedLesson?.module || "—"}
              </h3>
            </div>
            <div>
              <p className="text-sm text-gray-700 mb-1">
                Lesson {selectedLesson?.lessonDisplayId ?? "-"}
              </p>
              <h2 className="font-semibold mb-4">
                {selectedLesson?.lessonTitle || selectedLesson?.lesson || "—"}
              </h2>
            </div>
          </div>
        </div>

        {/* MAIN GRID */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 px-6 pb-6">
          {/* LEFT */}
          <div className="space-y-5">
            <div className="border rounded p-5">
              <label className="flex items-start text-sm font-semibold mb-2">
                Grade Posting Date
              </label>

              <DatePicker
                selected={
                  formData.gradePostingDate
                    ? new Date(formData.gradePostingDate)
                    : null
                }
                onChange={(date) =>
                  setFormData({
                    ...formData,
                    gradePostingDate: date,
                  })
                }
                minDate={
                  scheduleData?.start_date
                    ? new Date(scheduleData.start_date)
                    : new Date()
                }
                maxDate={
                  scheduleData?.end_date
                    ? new Date(scheduleData.end_date)
                    : null
                }
                dateFormat="MM/dd/yyyy"
                placeholderText="MM/DD/YYYY"
                className="w-72 border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4AACC5]"
              />
            </div>

            <div className="border rounded p-5">
              <h3 className="font-semibold mb-3">Submission Controls</h3>

              {[
                { field: "allowResubmission", label: "Allow Resubmission" },
                { field: "maxAttempts", label: "Max Attempts" },
                { field: "lateSubmission", label: "Late Submission" },
              ].map(({ field, label }) => (
                <label key={field} className="flex gap-2 mb-2">
                  <input
                    type="checkbox"
                    checked={formData[field]}
                    onChange={() => handleCheckbox(field)}
                  />
                  <span className="text-sm">{label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* MIDDLE */}
          <div className="border rounded p-5">
            {selectedDemo ? (
              <>
                <div className="flex justify-between mb-3">
                  <h3 className="font-semibold">{selectedDemo.title}</h3>
                  <button onClick={() => handleEditDemo(selectedDemo.demo_id)}>
                    <img src={Edit_icon} className="w-4 h-4" alt="Edit" />
                  </button>
                </div>

                <p className="text-sm text-gray-700 mb-4">
                  {selectedDemo.instructions || "—"}
                </p>

                {/* Display fetched dates if available */}
                {currentDemoAssignment && (
                  <div className="mb-4 space-y-2">
                    <div className="flex items-center gap-2">
                      <img src={Calendar} className="w-4 h-4" alt="Calendar" />
                      <span className="text-sm text-gray-700">
                        Start Date:{" "}
                        {currentDemoAssignment.start_date
                          ? new Date(
                              currentDemoAssignment.start_date,
                            ).toLocaleDateString()
                          : "—"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <img src={Calendar} className="w-4 h-4" alt="Calendar" />
                      <span className="text-sm text-gray-700">
                        End Date:{" "}
                        {currentDemoAssignment.end_date
                          ? new Date(
                              currentDemoAssignment.end_date,
                            ).toLocaleDateString()
                          : "—"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <img src={Calendar} className="w-4 h-4" alt="Calendar" />
                      <span className="text-sm text-gray-700">
                        Grade Posting Date:{" "}
                        {currentDemoAssignment.grade_posting_date
                          ? new Date(
                              currentDemoAssignment.grade_posting_date,
                            ).toLocaleDateString()
                          : "—"}
                      </span>
                    </div>
                  </div>
                )}

                {selectedDemo.file_url && (
                  <>
                    <div className="flex items-center gap-3 border rounded p-3 w-52">
                      <img src={pdfIcon} className="w-10 h-10" alt="PDF" />
                      <span className="text-xs break-all">
                        {selectedDemo.file_name ||
                          selectedDemo.file_url.split("/").pop() ||
                          "Reference File"}
                      </span>
                    </div>

                    <a
                      href={selectedDemo.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-[#4AACC5] underline mt-2 inline-block"
                    >
                      View Reference File
                    </a>
                  </>
                )}
              </>
            ) : (
              <p className="text-sm text-gray-500">Select Demo</p>
            )}
          </div>

          {/* RIGHT – ASSIGNMENT LIST */}
          <div className="border rounded p-5">
            <h3 className="font-semibold mb-4">Demo List</h3>

            {loading && <p className="text-sm">Loading...</p>}

            {!loading && lessonId && demos.length === 0 && (
              <div className="text-center space-y-3">
                <p className="text-sm text-gray-500">No Demo in this lesson</p>
                <button
                  onClick={handleCreateNewDemo}
                  className="px-4 py-2 bg-[#4AACC5] text-white rounded text-sm hover:bg-teal-500 transition"
                >
                  + Add Demo
                </button>
              </div>
            )}

            {!lessonId && (
              <p className="text-sm text-gray-500">No lesson selected</p>
            )}

            {demos.length > 0 && (
              <>
                {demos.map((item) => (
                  <div
                    key={item.demo_id}
                    onClick={() => {
                      setSelectedDemo(item);
                      fetchDemoAssignment(item.demo_id);
                    }}
                    className={`border rounded p-3 mb-2 cursor-pointer hover:border-[#4AACC5] transition ${
                      selectedDemo?.demo_id === item.demo_id
                        ? "border-[#4AACC5] bg-teal-50"
                        : ""
                    }`}
                  >
                    <div className="flex justify-between">
                      <p className="text-sm">{item.title}</p>
                      <span className="text-xs border rounded-full px-3 py-1">
                        {item.status || "Draft"}
                      </span>
                    </div>
                  </div>
                ))}

                {/* ✅ REMOVED: Add New Demo button when demo already exists */}
                {/* Only show "Add Demo" button when there are NO demos */}
              </>
            )}
          </div>
        </div>

        {/* Date Range Selection */}
        <div className="px-6 pb-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-xl">
            <div>
              <label className="flex items-start text-sm font-semibold text-gray-900 mb-2">
                Start Date <span className="text-red-600">*</span>
              </label>
              <DatePicker
                selected={
                  formData.startDate ? new Date(formData.startDate) : null
                }
                onChange={(date) =>
                  setFormData({ ...formData, startDate: date, endDate: null })
                }
                minDate={
                  scheduleData?.start_date
                    ? new Date(scheduleData.start_date) > new Date()
                      ? new Date(scheduleData.start_date)
                      : new Date()
                    : new Date()
                }
                maxDate={
                  scheduleData?.end_date
                    ? new Date(scheduleData.end_date)
                    : null
                }
                dateFormat="MM/dd/yyyy"
                placeholderText="MM/DD/YYYY"
                className="w-72 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#4AACC5]"
              />
            </div>

            <div>
              <label className="flex items-start text-sm font-semibold text-gray-900 mb-2">
                End Date <span className="text-red-600">*</span>
              </label>
              <DatePicker
                selected={formData.endDate ? new Date(formData.endDate) : null}
                onChange={(date) => setFormData({ ...formData, endDate: date })}
                minDate={
                  formData.startDate
                    ? new Date(
                        new Date(formData.startDate).setDate(
                          new Date(formData.startDate).getDate() + 1,
                        ),
                      )
                    : new Date()
                }
                maxDate={
                  scheduleData?.end_date
                    ? new Date(scheduleData.end_date)
                    : null
                }
                dateFormat="MM/dd/yyyy"
                placeholderText="MM/DD/YYYY"
                className="w-72 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#4AACC5]"
              />
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div className="px-6 pb-6 flex gap-3">
          <button
            onClick={onClose}
            className="border-2 border-[#4AACC5] px-6 py-2 rounded text-[#4AACC5] hover:bg-teal-50 transition"
          >
            Cancel
          </button>
          <button
            onClick={handlePublish}
            className="bg-[#4AACC5] text-white px-6 py-2 rounded hover:bg-teal-500 transition"
          >
            Publish
          </button>
        </div>
      </div>

      {/* Demo Create/Edit Modal - Pass publisher form data here */}
      {showEditPopup && (
        <AssignmentCreate
          isOpen={showEditPopup}
          onClose={() => {
            setShowEditPopup(false);
          }}
          selectedLesson={selectedLesson}
          demoData={selectedDemo}
          publisherFormData={formData} // ✅ Pass publisher form data
          onSave={handleDemoSaved}
        />
      )}

      {/* Toast Message */}
      {toast.show && (
        <ToastMessage
          type={toast.type}
          message={toast.message}
          onClose={() => setToast({ show: false, type: "error", message: "" })}
        />
      )}
    </div>
  );
}
